import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, CheckCircle, Brain, Zap, AlertTriangle, TrendingUp, ChevronDown } from "lucide-react";
import { Badge } from "../../ui/badge";

const patientData = {
  name: "Sarah Mitchell",
  age: 42,
  lastVisit: "Dec 2025",
  conditions: ["Moderate perio (#18-19)", "Fractured cusp #14", "Recurrent decay #30"],
};

const aiSteps = [
  { text: "Loading patient history & clinical data..." },
  { text: "Analyzing radiographs — periapical #14, bitewing R..." },
  { text: "Perio assessment: Stage II, Grade B — localized #18-19" },
  { text: "Evaluating tooth #14: fracture line extends subgingival" },
  { text: "Cross-referencing insurance — Delta Dental PPO" },
  { text: "Calculating optimal treatment sequence..." },
  { text: "Generating patient-friendly treatment narrative..." },
];

const PMSAITreatmentPlannerSlide = () => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [showPlan, setShowPlan] = useState(false);
  const [typedChars, setTypedChars] = useState(0);
  const [activeSection, setActiveSection] = useState(-1);
  const [showDiagram, setShowDiagram] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cycle = async () => {
      while (!cancelled) {
        setCurrentStep(-1);
        setShowPlan(false);
        setTypedChars(0);
        setActiveSection(-1);
        setShowDiagram(false);
        await new Promise((r) => setTimeout(r, 1500));

        for (let i = 0; i < aiSteps.length; i++) {
          if (cancelled) return;
          setCurrentStep(i);
          await new Promise((r) => setTimeout(r, 800));
        }

        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 500));
        setShowPlan(true);

        // Type the intro text
        const introText = "Based on your recent examination, we've identified 23 procedures that we recommend to protect your oral health. Here's what we found and why each step is important.";
        for (let c = 0; c <= introText.length; c += 3) {
          if (cancelled) return;
          setTypedChars(Math.min(c, introText.length));
          await new Promise((r) => setTimeout(r, 18));
        }
        setTypedChars(introText.length);

        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 600));
        setActiveSection(0);

        await new Promise((r) => setTimeout(r, 800));
        setShowDiagram(true);

        await new Promise((r) => setTimeout(r, 7000));
      }
    };
    cycle();
    return () => { cancelled = true; };
  }, []);

  const introText = "Based on your recent examination, we've identified 23 procedures that we recommend to protect your oral health. Here's what we found and why each step is important.";

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <Brain className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              AI{" "}
              <span className="text-blue-600">Treatment Planner</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              AI generates patient-friendly treatment plans with plain-language
              explanations, educational diagrams, and visual aids — so patients
              understand why each procedure matters and accept treatment faster.
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-2">
            {[
              "Patient-Friendly Language",
              "Visual Diagrams",
              "Educational Narratives",
              "Insurance-Aware",
              "Phased Sequencing",
              "One-Click Generate",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">10s</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Plan Generation
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">+34%</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Case Acceptance
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Side: AI Planner Interface */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
          >
            {/* Header */}
            <div className="h-12 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-900">Treatment Plan from Findings</span>
                  <span className="text-xs text-blue-600 font-medium">23 remaining</span>
                </div>
              </div>
              <Badge className="bg-blue-600 text-white hover:bg-blue-700 border-0">
                <Sparkles className="w-3 h-3 mr-1" />
                AI-Generated
              </Badge>
            </div>

            <div className="flex-1 overflow-y-auto">
              <AnimatePresence mode="wait">
                {!showPlan ? (
                  /* AI Reasoning Steps */
                  <motion.div
                    key="steps"
                    className="p-6 space-y-2"
                    exit={{ opacity: 0, y: -20, transition: { duration: 0.3 } }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <Sparkles className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm font-bold text-slate-700">
                        Generating treatment plan — {Math.min(currentStep + 1, aiSteps.length)} steps
                      </span>
                    </div>

                    {aiSteps.map((step, i) => {
                      const isActive = currentStep === i;
                      const isCompleted = currentStep > i;
                      const isVisible = currentStep >= i;

                      if (!isVisible) return null;

                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                          className={`flex items-center gap-3 py-2 px-3 rounded-lg transition-all ${
                            isActive ? "bg-blue-50 border border-blue-200" : ""
                          }`}
                        >
                          {isCompleted ? (
                            <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          ) : isActive ? (
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full flex-shrink-0"
                            />
                          ) : null}
                          <span className={`text-sm ${isCompleted ? "text-slate-500" : "text-slate-700 font-medium"}`}>
                            {step.text}
                          </span>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                ) : (
                  /* Generated Patient-Facing Treatment Plan */
                  <motion.div
                    key="plan"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="p-6 space-y-4"
                  >
                    {/* Title & Intro */}
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">
                        Your Treatment Plan — Why It Matters
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {introText.slice(0, typedChars)}
                        {typedChars < introText.length && (
                          <motion.span
                            animate={{ opacity: [1, 0] }}
                            transition={{ duration: 0.5, repeat: Infinity }}
                            className="inline-block w-0.5 h-3.5 bg-blue-600 ml-0.5 align-middle"
                          />
                        )}
                      </p>
                    </div>

                    {/* Gum Health Section */}
                    {activeSection >= 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-base">🦷</span>
                          <h4 className="text-base font-bold text-slate-900">Gum Health — Deep Cleaning</h4>
                        </div>

                        <p className="text-sm text-slate-600 leading-relaxed mb-3">
                          During your exam, we measured the spaces (called "pockets") between your teeth and gums.{" "}
                          <span className="font-bold text-slate-800">Healthy pockets are 1-3mm deep.</span>{" "}
                          We found several areas where these pockets have deepened to 5mm or more, which means
                          bacteria have built up below your gum line where regular brushing and flossing can't reach.
                        </p>

                        {/* Pocket Depth Diagram */}
                        {showDiagram && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="bg-blue-50 rounded-xl border border-blue-200 p-4 mb-3"
                          >
                            <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-3">Understanding Pocket Depth</p>

                            {/* Gumline label */}
                            <div className="flex items-center gap-2 mb-1 ml-8">
                              <div className="flex-1 border-b border-dashed border-slate-300" />
                              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Gumline</span>
                              <div className="flex-1 border-b border-dashed border-slate-300" />
                            </div>

                            <div className="flex items-start justify-center gap-8 py-3">
                              {/* Normal pocket */}
                              <div className="flex flex-col items-center gap-1">
                                <p className="text-[10px] text-slate-500 font-medium mb-1">Normal pocket</p>
                                <div className="relative w-12 flex flex-col items-center">
                                  <div className="w-8 h-6 bg-white rounded-t-lg border-2 border-slate-300" />
                                  <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: 20 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="w-1 bg-blue-300 rounded-b"
                                  />
                                  <div className="flex items-center gap-1 mt-1">
                                    <div className="w-3 h-px bg-slate-300" />
                                    <span className="text-xs font-bold text-blue-600">2mm</span>
                                    <div className="w-3 h-px bg-slate-300" />
                                  </div>
                                </div>
                                <span className="text-[10px] font-bold text-green-600 mt-1">Healthy</span>
                              </div>

                              {/* Moderate pocket */}
                              <div className="flex flex-col items-center gap-1">
                                <p className="text-[10px] text-slate-500 font-medium mb-1">Moderate pocket</p>
                                <div className="relative w-12 flex flex-col items-center">
                                  <div className="w-8 h-6 bg-white rounded-t-lg border-2 border-slate-300" />
                                  <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: 36 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                    className="w-1 bg-amber-400 rounded-b"
                                  />
                                  <div className="flex items-center gap-1 mt-1">
                                    <div className="w-3 h-px bg-slate-300" />
                                    <span className="text-xs font-bold text-amber-600">4mm</span>
                                    <div className="w-3 h-px bg-slate-300" />
                                  </div>
                                </div>
                                <span className="text-[10px] font-bold text-amber-600 mt-1">Watch</span>
                              </div>

                              {/* Deep pocket */}
                              <div className="flex flex-col items-center gap-1">
                                <p className="text-[10px] text-slate-500 font-medium mb-1">Deep pocket</p>
                                <div className="relative w-12 flex flex-col items-center">
                                  <div className="w-8 h-6 bg-white rounded-t-lg border-2 border-slate-300" />
                                  <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: 52 }}
                                    transition={{ duration: 0.6, delay: 0.6 }}
                                    className="w-1 bg-red-400 rounded-b"
                                  />
                                  <div className="flex items-center gap-1 mt-1">
                                    <div className="w-3 h-px bg-slate-300" />
                                    <span className="text-xs font-bold text-red-600">6mm</span>
                                    <div className="w-3 h-px bg-slate-300" />
                                  </div>
                                </div>
                                <span className="text-[10px] font-bold text-red-600 mt-1">Treatment Needed</span>
                              </div>
                            </div>

                            <p className="text-[11px] text-slate-500 leading-relaxed mt-2">
                              Pocket depths are measured in millimeters (mm). Deeper pockets allow bacteria to damage the bone supporting your teeth.
                            </p>
                          </motion.div>
                        )}

                        <p className="text-sm text-slate-600 leading-relaxed">
                          A <span className="font-bold text-slate-800">deep cleaning (scaling and root planing)</span>{" "}
                          removes this bacteria and smooths the root surfaces so your gums can heal and reattach.
                          Without treatment, gum disease can lead to bone loss, loose teeth, and eventually tooth loss.
                        </p>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Bottom Bar */}
            <div className="h-12 border-t border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between px-6">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-700">AI-generated patient education</span>
              </div>
              {showPlan && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-full text-xs font-bold shadow-md"
                >
                  <ChevronDown className="w-3 h-3" />
                  23 procedures remaining
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSAITreatmentPlannerSlide;
